import { useCallback, useState } from 'react';

import { Dot } from '@/components/OpsOnDutyBar/OpsOnDutyBar.styles';
import type { OpsSchedule } from '@/services/opsRota/types';
import styled from '@/styles';

import { shortDayLabel } from '../dates';
import { blockRangeSpaced, EMPTY_DAY } from '../messages';
import { C, Muted, SecondaryButton } from '../Operations.styles';

import BlockMenu from './BlockMenu';

// UI spec §8.2.
const Wrapper = styled('div', {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

const List = styled('ul', {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  margin: 0,
  padding: 0,
  listStyle: 'none',
});

const Block = styled('li', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  padding: '16px',
  border: `1px solid ${C.border}`,
  borderRadius: '12px',
  // A thin divider between blocks, centred in the 12px gap.
  '& + &::before': {
    content: '""',
    position: 'absolute',
    top: '-7.5px',
    left: '16px',
    right: '16px',
    height: '1px',
    background: C.softBorder,
  },
});

// `static`: the kebab menu (top 40, right 8) hangs off the block card, not
// this row, which the app's global `* { position: relative }` would make
// its containing block.
const Header = styled('div', {
  position: 'static',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '8px',
  minHeight: '24px',
});

// The range and, for a block carried over from the day before, the
// muted "From Oct 2" line above it. The kebab stays level with the top line.
const Title = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  minWidth: 0,
});

const From = styled('p', {
  margin: 0,
  fontSize: '14px',
  lineHeight: 1.4,
  color: C.muted,
});

const Range = styled('h4', {
  margin: 0,
  fontSize: '16px',
  fontWeight: 700,
  lineHeight: 1.4,
});

const NextDay = styled('span', { fontWeight: 400, color: C.muted });

const Names = styled('ul', { margin: 0, padding: 0, listStyle: 'none' });

const NameRow = styled('li', {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  minHeight: '38px',
  padding: '8px 16px 8px 0',
  fontSize: '16px',
});

const Inactive = styled('span', { fontSize: '14px', color: C.muted });

const Footer = styled('div', {
  display: 'flex',
  justifyContent: 'flex-end',
  minHeight: '36px',
});

type Props = {
  blocks: OpsSchedule[];
  /**
   * Past day (OP-14): no Add another. Each block's own menu follows
   * its `editable` flag instead (BR-07: a block still running stays
   * editable, whatever its date).
   */
  readOnly: boolean;
  onEdit: (s: OpsSchedule) => void;
  onRepeat: (s: OpsSchedule, trigger: HTMLElement | null) => void;
  /** `trigger` is the kebab, for returning focus when the dialog cancels. */
  onDelete: (s: OpsSchedule, trigger: HTMLElement | null) => void;
  onAddAnother: () => void;
};

// By the instant, not the clock: yesterday's 8 PM carry-over comes before
// today's 6 AM.
const byStart = (a: OpsSchedule, b: OpsSchedule) =>
  Date.parse(a.starts_at) - Date.parse(b.starts_at) || a.id - b.id;

// A day's blocks, one card per time range in start order, each with its
// managers and a kebab menu. One menu open at a time.
const DayBlocks = ({
  blocks,
  readOnly,
  onEdit,
  onRepeat,
  onDelete,
  onAddAnother,
}: Props) => {
  const [openId, setOpenId] = useState<number | null>(null);
  const close = useCallback(() => setOpenId(null), []);

  return (
    <Wrapper data-testid="opsDayBlocks">
      {blocks.length === 0 ? (
        <Muted data-testid="opsDayEmpty">{EMPTY_DAY}</Muted>
      ) : (
        <List>
          {[...blocks].sort(byStart).map((block) => (
            <Block
              key={block.id}
              data-testid={`opsScheduleBlock-${block.id}`}
              data-carry-over={block.carry_over || undefined}
              data-editable={block.editable}
            >
              <Header>
                <Title>
                  {block.carry_over && (
                    <From data-testid={`opsScheduleBlockFrom-${block.id}`}>
                      From {shortDayLabel(block.date)}
                    </From>
                  )}
                  <Range data-testid={`opsScheduleBlockRange-${block.id}`}>
                    {blockRangeSpaced(block)}
                    {block.crosses_midnight && <NextDay> (next day)</NextDay>}
                  </Range>
                </Title>
                <BlockMenu
                  schedule={block}
                  readOnly={!block.editable}
                  open={openId === block.id}
                  onToggle={() =>
                    setOpenId((id) => (id === block.id ? null : block.id))
                  }
                  onClose={close}
                  onEdit={onEdit}
                  onRepeat={onRepeat}
                  onDelete={onDelete}
                />
              </Header>
              <Names>
                {block.managers.map((m) => (
                  <NameRow
                    key={m.id}
                    data-testid={`opsScheduleBlockManager-${block.id}-${m.id}`}
                    data-active={m.active}
                  >
                    <Dot on={m.active} aria-hidden="true" />
                    {m.full_name}
                    {!m.active && <Inactive>(inactive)</Inactive>}
                  </NameRow>
                ))}
              </Names>
            </Block>
          ))}
        </List>
      )}
      {!readOnly && (
        <Footer>
          <SecondaryButton
            type="button"
            data-testid="opsListAddAnotherButton"
            onClick={onAddAnother}
          >
            Add another schedule
          </SecondaryButton>
        </Footer>
      )}
    </Wrapper>
  );
};

export default DayBlocks;
